// Vendor
import { FC, useMemo } from 'react';

// Hooks
import { useFolderPage } from 'src/components/folders/FolderPageContext';
import { useEntryById } from 'src/features/entries/hooks';

// Components
import { ProofListFooterActions } from 'src/components/folders/ProofListFooterActions';
import { ProofListTableProps } from 'src/components/folders/ProofListTable';

// Enums
import { ProofResultEnum } from 'src/ts/enums';
import { DocumentOrProof } from '../../types';
import { OFFER_LETTER_TYPE } from 'src/components/folders/ModernizedFraudAnalystReview/helpers/proofHelpers';
import { IProof } from 'src/ts/interfaces';

type FooterActionsProps = Pick<ProofListTableProps, 'proofs'> & {
  showRequestDocumentsButton?: boolean;
  isReviewDisabled?: boolean;
  onEscalate?: () => void;
  canEscalate?: boolean;
  isLoadingEscalate?: boolean;
  isDisabled?: boolean;
};

export const FooterActions: FC<FooterActionsProps> = (props: FooterActionsProps) => {
  const {
    proofs,
    showRequestDocumentsButton,
    isReviewDisabled,
    onEscalate,
    canEscalate,
    isLoadingEscalate,
    isDisabled
  } = props;

  const { selectedEntryId, setShowSendReportModal, setShowRequestDocumentsModal } = useFolderPage();
  const { entry } = useEntryById(selectedEntryId as string);

  const combinedDocuments: DocumentOrProof[] = useMemo(
    () => [...(entry?.document_reviews || []), ...proofs],
    [entry?.document_reviews, proofs]
  );

  const allDocumentsHaveResult = useMemo(
    () => combinedDocuments?.every((doc) => doc.result !== ProofResultEnum.Pending),
    [combinedDocuments]
  );

  const allDocumentsAreUndetermined = useMemo(
    () => combinedDocuments?.every((doc) => doc.result === ProofResultEnum.Undetermined),
    [combinedDocuments]
  );

  const isOfferLetterOnly = useMemo(
    () =>
      combinedDocuments.length > 0 &&
      combinedDocuments.every((d): d is IProof => 'type' in d && d.type === OFFER_LETTER_TYPE),
    [combinedDocuments]
  );

  // Sole-OL submissions bypass the all-UNDETERMINED safety check because the analyst's
  // OLV ruling produces UNDETERMINED as a terminal state. The adjacent
  // isRequestDocumentsDisabled gate deliberately does NOT bypass — the two actions are
  // independent. (ITL-2762)
  const isSendReportDisabled =
    isReviewDisabled ||
    !(allDocumentsHaveResult && (isOfferLetterOnly || !allDocumentsAreUndetermined));

  return (
    <ProofListFooterActions
      entry={entry}
      isSendReportDisabled={isSendReportDisabled}
      isRequestDocumentsDisabled={!allDocumentsAreUndetermined || isReviewDisabled}
      onSendReport={() => setShowSendReportModal?.(true)}
      onRequestDocuments={() => setShowRequestDocumentsModal?.(true)}
      showRequestDocumentsButton={showRequestDocumentsButton}
      canEscalate={canEscalate}
      onEscalate={onEscalate}
      isLoadingEscalate={isLoadingEscalate}
      isDisabled={isDisabled}
    />
  );
};

export default FooterActions;
